import React, { useState, useEffect, useCallback } from 'react';
import './ProductForm.css';

/* ── Helpers ─────────────────────────────────────────── */
const genCode = () => 'PRD-' + Math.floor(10000 + Math.random() * 90000);

const getStockLabel = (unit) => {
    switch (unit?.toLowerCase()) {
        case 'kg': return 'en Kg';
        case 'gramos': return 'en Gramos';
        case 'litros': return 'en Litros';
        case 'paquete': return 'en Paquetes';
        case 'saco': return 'en Sacos';
        case 'caja': return 'en Cajas';
        default: return 'en Unidades';
    }
};
const getPriceLabel = (unit) => {
    switch (unit?.toLowerCase()) {
        case 'kg': return 'por Kg';
        case 'gramos': return 'por Gramo';
        case 'litros': return 'por Litro';
        case 'paquete': return 'por Paquete';
        case 'saco': return 'por Saco';
        case 'caja': return 'por Caja';
        default: return 'por Unidad';
    }
};

/* ── Lote vacío ──────────────────────────────────────── */
const emptyLote = () => ({ id: Date.now(), numero: '', fecha_vencimiento: '', cantidad: 0, ubicacion: '' });

/* ══════════════════════════════════════════════════════
   COMPONENTE
══════════════════════════════════════════════════════ */
const ProductForm = ({ initialData, onSubmit, onCancel, isSaving, isBotica = false, isJuguteria = false }) => {

    const isEditing = !!initialData;

    const [formData, setFormData] = useState({
        name: '',
        code: genCode(),
        category: '',
        laboratorio: '',
        nombre_comercial: '',
        titular: '',
        presentacion: '',
        forma_farmaceutica: '',
        concentracion: '',
        principio_activo: '',
        codigo_barras: '',
        description: '',
        sale_price: '',
        purchase_price: '',
        stock: 0,
        min_stock: 5,
        unit: 'unidad',
        fraccionada: 0,
        status: 'active',
        nivel_dificultad: '',
        numero_piezas: '',
        material: '',
    });

    const [lotes, setLotes] = useState([emptyLote()]);

    /* ── Populate on edit ── */
    useEffect(() => {
        if (initialData) {
            const unit = initialData.unit || 'unidad';
            const isDecimal = ['kg', 'gramos', 'litros'].includes(unit.toLowerCase());
            setFormData({
                name: initialData.name || '',
                code: initialData.code || '',
                category: initialData.category || '',
                laboratorio: initialData.laboratorio || '',
                nombre_comercial: initialData.nombre_comercial || '',
                titular: initialData.titular || '',
                presentacion: initialData.presentacion || '',
                forma_farmaceutica: initialData.forma_farmaceutica || '',
                concentracion: initialData.concentracion || '',
                principio_activo: initialData.principio_activo || '',
                codigo_barras: initialData.codigo_barras || '',
                description: initialData.description || '',
                sale_price: initialData.sale_price ?? initialData.price ?? '',
                purchase_price: initialData.purchase_price ?? '',
                stock: isDecimal ? (initialData.stock || 0) : Math.trunc(initialData.stock || 0),
                min_stock: isDecimal ? (initialData.min_stock || 5) : Math.trunc(initialData.min_stock || 5),
                unit,
                fraccionada: initialData.fraccionada || 0,
                status: initialData.is_active !== false ? 'active' : 'inactive',
                nivel_dificultad: initialData.nivel_dificultad || '',
                numero_piezas: initialData.numero_piezas ?? '',
                material: initialData.material || '',
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    /* ── Lotes helpers ── */
    const addLote = () => setLotes(prev => [...prev, emptyLote()]);
    const removeLote = (id) => setLotes(prev => prev.filter(l => l.id !== id));
    const updateLote = (id, field, value) =>
        setLotes(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));

    /* ── Keyboard shortcuts ── */
    useEffect(() => {
        const handler = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                document.getElementById('pf-submit-btn')?.click();
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'l') {
                e.preventDefault();
                addLote();
            }
            if (e.key === 'Escape' && onCancel) {
                onCancel();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onCancel]);

    /* ── Submit ── */
    const handleSubmit = (e) => {
        e.preventDefault();

        const finalStock = parseFloat(formData.stock) || 0;

        // Validar lotes si hay stock inicial en modo botica
        if (!isEditing && finalStock > 0 && isBotica) {
            const lotesValidos = lotes.filter(l => l.numero.trim() && l.fecha_vencimiento);
            if (lotesValidos.length === 0) {
                alert('⚠️ Ingrese al menos un lote con número y fecha de vencimiento para el stock inicial.');
                return;
            }
        }

        const dataToSend = {
            ...formData,
            stock: finalStock,
            is_active: formData.status === 'active',
            numero_piezas: (formData.numero_piezas !== '' && formData.numero_piezas != null) ? parseInt(formData.numero_piezas, 10) : null,
            nivel_dificultad: formData.nivel_dificultad || null,
        };
        delete dataToSend.status;

        if (isEditing) {
            delete dataToSend.stock;
        } else if (isBotica && lotes.some(l => l.numero.trim())) {
            // Incluir primer lote como campos legacy si el backend lo espera
            const primerLote = lotes.find(l => l.numero.trim());
            if (primerLote) {
                dataToSend.numero_lote = primerLote.numero;
                dataToSend.fecha_vencimiento = primerLote.fecha_vencimiento;
            }
            dataToSend.lotes = lotes.filter(l => l.numero.trim()).map(l => ({
                numero: l.numero,
                fecha_vencimiento: l.fecha_vencimiento,
                cantidad_inicial: parseFloat(l.cantidad) || 0,
                ubicacion: l.ubicacion,
            }));
        }

        onSubmit(dataToSend);
    };

    /* ── Computed values ── */
    const isDecimalUnit = ['kg', 'gramos', 'litros'].includes(formData.unit?.toLowerCase());
    const purchase = parseFloat(formData.purchase_price) || 0;
    const sale = parseFloat(formData.sale_price) || 0;
    const ganancia = sale - purchase;
    const gananciaPct = purchase > 0 ? ((ganancia / purchase) * 100).toFixed(0) : 0;
    const priceLabel = getPriceLabel(formData.unit);

    /* ── Sidebar preview ── */
    const previewName = formData.name || (isJuguteria ? 'Nombre del armable' : isBotica ? 'Nombre del medicamento' : 'Nombre del producto');
    const previewSub = isJuguteria
        ? ([formData.numero_piezas ? `🧩 ${formData.numero_piezas} piezas` : null, formData.nivel_dificultad ? `🎮 Nivel ${formData.nivel_dificultad}` : null, formData.material ? `🪵 ${formData.material}` : null].filter(Boolean).join(' • ') || 'Armable / Set')
        : ([formData.presentacion, formData.concentracion].filter(Boolean).join(' – ') || 'Presentación – Concentración');
    const previewLab = isJuguteria
        ? (formData.category || 'Jugutería')
        : (formData.laboratorio || 'Laboratorio / Marca');


    /* ══ RENDER ════════════════════════════════════════ */
    return (
        <div className="pf-page">

            {/* ── TOP BAR ── */}
            <div className="pf-topbar">
                <div className="pf-topbar-left">
                    {onCancel && (
                        <button type="button" className="pf-back-btn" onClick={onCancel} title="Volver (Esc)">
                            ←
                        </button>
                    )}
                    <div className="pf-topbar-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                            <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                        </svg>
                    </div>
                    <div>
                        <div className="pf-topbar-title">
                            {isEditing
                                ? (isJuguteria ? 'Editar Armable' : isBotica ? 'Editar Medicamento' : 'Editar Producto')
                                : (isJuguteria ? 'Nuevo Armable' : isBotica ? 'Nuevo Medicamento' : 'Nuevo Producto')}
                        </div>
                        <div className="pf-topbar-sub">
                            {isEditing ? 'Modificar datos del producto' : (isJuguteria ? 'Agregar set o armable al inventario' : 'Agregar producto al inventario')}
                        </div>
                    </div>
                </div>
                <div className="pf-topbar-actions">
                    {onCancel && (
                        <button type="button" className="pf-btn-cancel" onClick={onCancel}>
                            ✕ Cancelar
                        </button>
                    )}
                    <button
                        id="pf-submit-btn"
                        type="button"
                        className="pf-btn-save"
                        onClick={handleSubmit}
                        disabled={isSaving}
                    >
                        {isSaving ? '⏳ Guardando...' : `💾 ${isEditing ? 'Actualizar' : 'Guardar'} ${isJuguteria ? 'Armable' : isBotica ? 'Medicamento' : 'Producto'}`}
                    </button>
                </div>
            </div>

            {/* ── INFO BANNER ── */}
            <div className="pf-banner">
                ℹ️ Completa la información del {isJuguteria ? 'armable / set de construcción' : isBotica ? 'medicamento' : 'producto'} para mantener un inventario ordenado y actualizado.
            </div>

            {/* ── BODY (form + sidebar) ── */}
            <form onSubmit={handleSubmit} className="pf-body">

                {/* ════════════════════════════════════════
                    COLUMNA PRINCIPAL — SECCIONES
                ════════════════════════════════════════ */}
                <div className="pf-main">

                    {/* ── SECCIÓN 1: INFORMACIÓN BÁSICA ── */}
                    <div className="pf-section">
                        <div className="pf-section-hdr">
                            <span className="pf-section-num">1</span>
                            INFORMACIÓN BÁSICA
                        </div>

                        {/* Nombre + Código */}
                        <div className="pf-row pf-row--2-1">
                            <div className="pf-field">
                                <label className="pf-label">
                                    {isJuguteria ? 'Nombre del Armable / Set' : isBotica ? 'Nombre del Medicamento / Comercial' : 'Nombre del Producto'} <span className="pf-req">*</span>
                                </label>
                                <div className="pf-input-wrap">
                                    <span className="pf-input-icon">{isJuguteria ? '🧩' : '🔍'}</span>
                                    <input
                                        type="text"
                                        name="name"
                                        className="pf-input pf-input--icon"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        placeholder={isJuguteria ? 'Ej: Halcón Milenario Star Wars, Castillo Medieval 3D, Bugatti Chiron...' : isBotica ? 'Ej: Panadol, Amoxil, Augmentin, Amoxicilina...' : 'Ej: Arroz Casteño 1kg, Aceite Primor 1L...'}
                                    />
                                </div>
                                {isBotica && (
                                    <small style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
                                        ℹ️ Escribe el nombre comercial o genérico con el que lo conoces y buscas
                                    </small>
                                )}
                                {isJuguteria && (
                                    <small style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
                                        ℹ️ Nombre del modelo, set o temática del armable
                                    </small>
                                )}
                            </div>
                            <div className="pf-field">
                                <label className="pf-label">Código Interno (SKU) <span className="pf-req">*</span></label>
                                <div className="pf-input-wrap">
                                    <input
                                        type="text"
                                        name="code"
                                        className="pf-input"
                                        value={formData.code}
                                        onChange={handleChange}
                                        placeholder="Ej: PRD-52966"
                                    />
                                    <span className="pf-input-icon-right">▌▌▌</span>
                                </div>
                            </div>
                        </div>

                        {/* ── ESPECIFICACIONES DEL ARMABLE (Solo Jugutería) ── */}
                        {isJuguteria && (
                            <div className="pf-row pf-row--2" style={{ background: 'rgba(99, 102, 241, 0.05)', padding: '14px', borderRadius: '10px', border: '1px solid rgba(99, 102, 241, 0.15)', marginBottom: '14px' }}>
                                <div className="pf-field">
                                    <label className="pf-label" style={{ fontWeight: 700, color: '#4338ca' }}>
                                        🧩 Número de Piezas
                                    </label>
                                    <div className="pf-input-wrap">
                                        <input
                                            type="number"
                                            name="numero_piezas"
                                            className="pf-input pf-input--icon"
                                            value={formData.numero_piezas}
                                            onChange={handleChange}
                                            min="1"
                                            placeholder="Ej: 850 (piezas del set)"
                                        />
                                        <span className="pf-input-icon">🧱</span>
                                    </div>
                                    <small style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
                                        Cantidad de bloques o piezas que incluye el set
                                    </small>
                                </div>

                                <div className="pf-field">
                                    <label className="pf-label" style={{ fontWeight: 700, color: '#4338ca' }}>
                                        🎮 Nivel de Dificultad
                                    </label>
                                    <div className="pf-input-wrap">
                                        <select
                                            name="nivel_dificultad"
                                            className="pf-select"
                                            value={formData.nivel_dificultad}
                                            onChange={handleChange}
                                        >
                                            <option value="">-- Seleccionar Nivel --</option>
                                            <option value="principiante">🟢 Principiante (Fácil / Niños / Principiantes)</option>
                                            <option value="medio">🟡 Medio (Intermedio / Jóvenes)</option>
                                            <option value="avanzado">🔴 Avanzado (Complejo / Expertos / Coleccionistas)</option>
                                        </select>
                                    </div>
                                    <small style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
                                        Clasificación para recomendar al cliente según su destreza
                                    </small>
                                </div>

                                {/* ── Material (ancho completo) ── */}
                                <div className="pf-field" style={{ gridColumn: '1 / -1' }}>
                                    <label className="pf-label" style={{ fontWeight: 700, color: '#4338ca' }}>
                                        🪵 Material
                                    </label>
                                    <div className="pf-input-wrap">
                                        <input
                                            type="text"
                                            name="material"
                                            className="pf-input pf-input--icon"
                                            value={formData.material}
                                            onChange={handleChange}
                                            placeholder="Ej: Microporoso, Madera, Plástico ABS..."
                                            list="material-list"
                                            autoComplete="off"
                                        />
                                        <span className="pf-input-icon">🪵</span>
                                        <datalist id="material-list">
                                            <option value="Microporoso" />
                                            <option value="Madera" />
                                            <option value="Plástico" />
                                            <option value="Metal" />
                                            <option value="Tela" />
                                            <option value="Goma" />
                                        </datalist>
                                    </div>
                                    <small style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
                                        Material principal del que está fabricado el juguete
                                    </small>
                                </div>
                            </div>
                        )}

                        {/* Lab / Presentación / Concentración — SOLO BOTICA */}
                        {isBotica && (
                            <>
                                {/* Fila 1: Laboratorio + Principio Activo */}
                                <div className="pf-row pf-row--2">
                                    <div className="pf-field">
                                        <label className="pf-label">Laboratorio / Marca <span className="pf-req">*</span></label>
                                        <div className="pf-input-wrap">
                                            <input
                                                type="text"
                                                name="laboratorio"
                                                className="pf-input pf-input--icon"
                                                value={formData.laboratorio}
                                                onChange={handleChange}
                                                placeholder="Escribe el nombre del laboratorio..."
                                                list="lab-list"
                                                autoComplete="off"
                                            />
                                            <span className="pf-input-icon">🏧</span>
                                            <datalist id="lab-list">
                                                <option value="Genfar" />
                                                <option value="MK" />
                                                <option value="La Santé" />
                                                <option value="Medifarma" />
                                                <option value="Bagó" />
                                                <option value="Pfizer" />
                                                <option value="Bayer" />
                                                <option value="Abbott" />
                                                <option value="Roche" />
                                                <option value="Hersil" />
                                                <option value="Farmindustria" />
                                                <option value="Ac Farma" />
                                                <option value="Alfaro" />
                                                <option value="Elifarma" />
                                                <option value="Portugal" />
                                                <option value="Teva" />
                                                <option value="Novartis" />
                                                <option value="GlaxoSmithKline" />
                                                <option value="Sanofi" />
                                                <option value="AstraZeneca" />
                                                <option value="Johnson & Johnson" />
                                                <option value="Merck" />
                                                <option value="IQFARMA" />
                                                <option value="Tecnofarma" />
                                                <option value="Farmakonsuma" />
                                                <option value="Genoma Lab" />
                                                <option value="InkaSalud" />
                                                <option value="Laboratorio Roemmers" />
                                                <option value="Mark" />
                                            </datalist>
                                        </div>
                                        <small style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
                                            💡 Puedes escribir cualquier laboratorio aunque no esté en la lista
                                        </small>
                                    </div>
                                    <div className="pf-field">
                                        <label className="pf-label">Principio Activo (INN)</label>
                                        <div className="pf-input-wrap">
                                            <input
                                                type="text"
                                                name="principio_activo"
                                                className="pf-input pf-input--icon"
                                                value={formData.principio_activo}
                                                onChange={handleChange}
                                                placeholder="Ej: Paracetamol, Amoxicilina, Ibuprofeno..."
                                            />
                                            <span className="pf-input-icon">🔬</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Fila 2: Presentación + Forma farmacéutica + Concentración */}
                                <div className="pf-row pf-row--3">
                                    <div className="pf-field">
                                        <label className="pf-label">Presentación Farmacéutica <span className="pf-req">*</span></label>
                                        <div className="pf-input-wrap">
                                            <input
                                                type="text"
                                                name="presentacion"
                                                className="pf-input pf-input--icon"
                                                value={formData.presentacion}
                                                onChange={handleChange}
                                                list="presentaciones-list"
                                                placeholder="Ej: Caja x 100 tabletas, Frasco 120mL..."
                                            />
                                            <span className="pf-input-icon">💊</span>
                                            <datalist id="presentaciones-list">
                                                <option value="Caja x 10 tabletas" />
                                                <option value="Caja x 20 tabletas" />
                                                <option value="Caja x 30 tabletas" />
                                                <option value="Caja x 100 tabletas" />
                                                <option value="Blister x 10 tabletas" />
                                                <option value="Frasco x 100mL" />
                                                <option value="Frasco x 120mL" />
                                                <option value="Frasco x 250mL" />
                                                <option value="Ampolla x 1mL" />
                                                <option value="Ampolla x 2mL" />
                                                <option value="Tubo 20g" />
                                                <option value="Tubo 30g" />
                                                <option value="Sobre x 1g" />
                                            </datalist>
                                        </div>
                                    </div>
                                    <div className="pf-field">
                                        <label className="pf-label">Forma Farmacéutica</label>
                                        <div className="pf-input-wrap">
                                            <input
                                                type="text"
                                                name="forma_farmaceutica"
                                                className="pf-input pf-input--icon"
                                                value={formData.forma_farmaceutica}
                                                onChange={handleChange}
                                                list="forma-farm-list"
                                                placeholder="Ej: Comprimido, Jarabe, Cápsula..."
                                            />
                                            <span className="pf-input-icon">🧪</span>
                                            <datalist id="forma-farm-list">
                                                <option value="Comprimido" />
                                                <option value="Comprimido recubierto" />
                                                <option value="Cápsula" />
                                                <option value="Cápsula blanda" />
                                                <option value="Solución oral" />
                                                <option value="Jarabe" />
                                                <option value="Suspensión oral" />
                                                <option value="Inyectable" />
                                                <option value="Solución inyectable" />
                                                <option value="Pomada" />
                                                <option value="Crema" />
                                                <option value="Gel" />
                                                <option value="Parche transdérmico" />
                                                <option value="Polvo para suspensión" />
                                                <option value="Granulado" />
                                                <option value="Supositorio" />
                                                <option value="Óvulo" />
                                                <option value="Gota oftálmica" />
                                                <option value="Inhalador" />
                                                <option value="Aerosol" />
                                            </datalist>
                                        </div>
                                    </div>
                                    <div className="pf-field">
                                        <label className="pf-label">Concentración / Dosis <span className="pf-req">*</span></label>
                                        <div className="pf-input-wrap">
                                            <input
                                                type="text"
                                                name="concentracion"
                                                className="pf-input pf-input--icon"
                                                value={formData.concentracion}
                                                onChange={handleChange}
                                                placeholder="Ej: 500mg, 250mg/5ml, 10mg/mL..."
                                            />
                                            <span className="pf-input-icon">🧪</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Fila 3: Código de barras (ocupa todo el ancho ahora que principio activo subió) */}
                                <div className="pf-row pf-row--1">
                                    <div className="pf-field">
                                        <label className="pf-label">Código de Barras / EAN-13</label>
                                        <div className="pf-input-wrap">
                                            <input
                                                type="text"
                                                name="codigo_barras"
                                                className="pf-input pf-input--icon"
                                                value={formData.codigo_barras}
                                                onChange={handleChange}
                                                placeholder="Ej: 7751058900007 (escanear o digitar)"
                                                maxLength={50}
                                            />
                                            <span className="pf-input-icon">▌▌▌</span>
                                        </div>
                                    </div>
                                </div>

                            </>
                        )}
                        <div className="pf-row pf-row--3">
                            <div className="pf-field">
                                <label className="pf-label">Unidad de Medida / Venta <span className="pf-req">*</span></label>
                                <select name="unit" className="pf-select" value={formData.unit} onChange={handleChange}>
                                    <option value="unidad">Unidad</option>
                                    {isBotica && <option value="caja">Caja</option>}
                                    {isBotica && <option value="blister">Blíster</option>}
                                    {isBotica && <option value="frasco">Frasco</option>}
                                    {isBotica && <option value="tubo">Tubo</option>}
                                    {isBotica && <option value="ampolla">Ampolla</option>}
                                    <option value="kg">Kilogramos (Kg)</option>
                                    <option value="gramos">Gramos (g)</option>
                                    <option value="litros">Litros (L)</option>
                                    <option value="paquete">Paquete</option>
                                    <option value="saco">Saco</option>
                                </select>
                            </div>
                            <div className="pf-field">
                                <label className="pf-label">Estado <span className="pf-req">*</span></label>
                                <select name="status" className="pf-select" value={formData.status} onChange={handleChange}>
                                    <option value="active">● Activo (Disponible para venta)</option>
                                    <option value="inactive">● Inactivo</option>
                                </select>
                            </div>
                            {isBotica && (
                                <div className="pf-field">
                                    <label className="pf-label pf-label--blue">Contenido del envase (Fraccionamiento)</label>
                                    <div className="pf-input-wrap">
                                        <input
                                            type="number"
                                            name="fraccionada"
                                            className="pf-input pf-input--blue pf-input--icon"
                                            value={formData.fraccionada}
                                            onChange={handleChange}
                                            min="0"
                                            onFocus={e => e.target.select()}
                                            placeholder="0"
                                        />
                                        <span className="pf-input-icon">📦</span>
                                    </div>
                                    <small style={{ fontSize: '0.73rem', color: '#64748b', marginTop: '2px', display: 'block' }}>Ej: Caja x 100 tabletas → escribir 100</small>
                                </div>
                            )}
                            {!isBotica && (
                                <div className="pf-field">
                                    <label className="pf-label">Categoría / Familia</label>
                                    <input
                                        type="text"
                                        name="category"
                                        className="pf-input"
                                        value={formData.category}
                                        onChange={handleChange}
                                        list="categories-list"
                                        placeholder="Seleccione o escriba..."
                                    />
                                    <datalist id="categories-list">
                                        {isJuguteria ? (
                                            <>
                                                <option value="Lego y Bloques de Construcción" />
                                                <option value="Maquetas y Modelismo" />
                                                <option value="Puzzles y Rompecabezas 3D" />
                                                <option value="Robótica y Mecánica" />
                                                <option value="Vehículos y Autos a Escala" />
                                                <option value="Arquitectura y Monumentos" />
                                                <option value="Figuras y Personajes Armables" />
                                                <option value="Armables de Madera" />
                                                <option value="Armables Metálicos" />
                                                <option value="Accesorios y Repuestos" />
                                            </>
                                        ) : (
                                            <>
                                                <option value="Abarrotes" />
                                                <option value="Bebidas y Gaseosas" />
                                                <option value="Lácteos y Derivados" />
                                                <option value="Limpieza y Hogar" />
                                                <option value="Carnes y Embutidos" />
                                                <option value="Snacks y Golosinas" />
                                                <option value="Panadería y Repostería" />
                                                <option value="Frutas y Verduras" />
                                                <option value="Cuidado Personal" />
                                                <option value="Licores" />
                                            </>
                                        )}
                                    </datalist>
                                </div>
                            )}
                        </div>

                        {/* Categoría en modo botica */}
                        {isBotica && (
                            <div className="pf-row pf-row--1">
                                <div className="pf-field">
                                    <label className="pf-label">Categoría / Familia</label>
                                    <input
                                        type="text"
                                        name="category"
                                        className="pf-input"
                                        value={formData.category}
                                        onChange={handleChange}
                                        list="cat-botica-list"
                                        placeholder="Ej: Analgésicos, Antibióticos, Gastroenterología..."
                                    />
                                    <datalist id="cat-botica-list">
                                        <option value="Analgésicos y Antiinflamatorios" />
                                        <option value="Antibióticos" />
                                        <option value="Antigripales y Respiratorios" />
                                        <option value="Gastroenterología" />
                                        <option value="Vitaminas y Suplementos" />
                                        <option value="Cuidado Personal" />
                                        <option value="Primeros Auxilios" />
                                        <option value="Maternidad y Bebés" />
                                        <option value="Dermatología" />
                                        <option value="Cardiovascular" />
                                        <option value="Diabetes" />
                                        <option value="Antiparasitarios" />
                                    </datalist>
                                </div>
                            </div>
                        )}

                        {/* Observaciones (para productos NO-botica) + descripción */}
                        {!isBotica && (
                            <div className="pf-row pf-row--1">
                                <div className="pf-field">
                                    <label className="pf-label">Observaciones</label>
                                    <textarea
                                        name="description"
                                        className="pf-textarea"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows={2}
                                        placeholder="Ej: Información adicional del producto..."
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── SECCIÓN 2: PRECIOS Y COSTOS ── */}
                    <div className="pf-section">
                        <div className="pf-section-hdr">
                            <span className="pf-section-num">2</span>
                            PRECIOS Y COSTOS
                        </div>

                        <div className="pf-row pf-row--3">
                            <div className="pf-field">
                                <label className="pf-label">Precio de Compra ({priceLabel}) <span className="pf-req">*</span></label>
                                <div className="pf-price-wrap">
                                    <span className="pf-price-prefix">S/.</span>
                                    <input
                                        type="number" step="0.01" min="0"
                                        name="purchase_price"
                                        className="pf-price-input"
                                        value={formData.purchase_price}
                                        onChange={handleChange}
                                        onFocus={e => e.target.select()}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                            <div className="pf-field">
                                <label className="pf-label">Precio de Venta ({priceLabel}) <span className="pf-req">*</span></label>
                                <div className="pf-price-wrap">
                                    <span className="pf-price-prefix">S/.</span>
                                    <input
                                        type="number" step="0.01" min="0"
                                        name="sale_price"
                                        className="pf-price-input"
                                        value={formData.sale_price}
                                        onChange={handleChange}
                                        onFocus={e => e.target.select()}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                            <div className="pf-field">
                                <label className="pf-label">Ganancia Estimada ({priceLabel})</label>
                                <div className="pf-price-wrap">
                                    <span className="pf-price-prefix">S/.</span>
                                    <input
                                        type="text"
                                        className="pf-price-input pf-price-input--readonly"
                                        value={ganancia.toFixed(2)}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Ganancia info bar */}
                        <div className="pf-profit-bar">
                            <span className="pf-profit-label">
                                ℹ️ Ganancia % estimada: <strong>{gananciaPct}%</strong>
                            </span>
                            <span className="pf-profit-formula">
                                Fórmula: (Precio Venta - Precio Compra) / Precio Compra x 100
                            </span>
                        </div>
                    </div>

                    {/* ── SECCIÓN 3: INVENTARIO Y LOTES ── */}
                    <div className="pf-section">
                        <div className="pf-section-hdr">
                            <span className="pf-section-num">3</span>
                            INVENTARIO Y LOTES
                        </div>

                        {isEditing ? (
                            /* Modo edición — stock bloqueado */
                            <div className="pf-stock-locked">
                                <span className="pf-lock-icon">🔒</span>
                                <div>
                                    <strong>Stock actual: {formData.stock} {formData.unit || 'uds'}</strong>
                                    <p>El stock se actualiza mediante {isBotica ? 'Recepción de Lotes o ' : ''}<strong>Ajuste de Inventario</strong> para mantener trazabilidad.</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="pf-row pf-row--2">
                                    <div className="pf-field">
                                        <label className="pf-label">Stock Inicial ({getStockLabel(formData.unit)}) <span className="pf-req">*</span></label>
                                        <div className="pf-input-wrap">
                                            <span className="pf-input-icon">📦</span>
                                            <input
                                                type="number"
                                                step={isDecimalUnit ? '0.01' : '1'}
                                                name="stock"
                                                className="pf-input pf-input--icon"
                                                value={formData.stock}
                                                onChange={handleChange}
                                                onFocus={e => e.target.select()}
                                                placeholder="0"
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                    <div className="pf-field">
                                        <label className="pf-label">Stock Mínimo (Alerta de reposición) <span className="pf-req">*</span></label>
                                        <div className="pf-input-wrap">
                                            <span className="pf-input-icon">🔔</span>
                                            <input
                                                type="number"
                                                step={isDecimalUnit ? '0.01' : '1'}
                                                name="min_stock"
                                                className="pf-input pf-input--icon"
                                                value={formData.min_stock}
                                                onChange={handleChange}
                                                onFocus={e => e.target.select()}
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Control de lotes */}
                                {isBotica && (
                                    <div className="pf-lotes-section">
                                        <div className="pf-lotes-title">CONTROL DE LOTES <span>(Opcional)</span></div>
                                        <div className="pf-lotes-table">
                                            <div className="pf-lotes-thead">
                                                <span>Lote</span>
                                                <span>Fecha de Vencimiento</span>
                                                <span>Cantidad Inicial</span>
                                                <span>Ubicación</span>
                                                <span>Acciones</span>
                                            </div>
                                            {lotes.map(lote => (
                                                <div key={lote.id} className="pf-lote-row">
                                                    <input
                                                        type="text"
                                                        className="pf-input"
                                                        placeholder="Ej: L2405A"
                                                        value={lote.numero}
                                                        onChange={e => updateLote(lote.id, 'numero', e.target.value)}
                                                    />
                                                    <input
                                                        type="date"
                                                        className="pf-input"
                                                        value={lote.fecha_vencimiento}
                                                        onChange={e => updateLote(lote.id, 'fecha_vencimiento', e.target.value)}
                                                    />
                                                    <input
                                                        type="number"
                                                        className="pf-input"
                                                        placeholder="0"
                                                        min="0"
                                                        value={lote.cantidad}
                                                        onChange={e => updateLote(lote.id, 'cantidad', e.target.value)}
                                                        onFocus={e => e.target.select()}
                                                    />
                                                    <div className="pf-input-wrap">
                                                        <span className="pf-input-icon">📍</span>
                                                        <input
                                                            type="text"
                                                            className="pf-input pf-input--icon"
                                                            placeholder="Ej: Anaquel 1 - A"
                                                            value={lote.ubicacion}
                                                            onChange={e => updateLote(lote.id, 'ubicacion', e.target.value)}
                                                        />
                                                    </div>
                                                    {lotes.length > 1 && (
                                                        <button type="button" className="pf-lote-del" onClick={() => removeLote(lote.id)} title="Eliminar lote">
                                                            🗑
                                                        </button>
                                                    )}
                                                    {lotes.length === 1 && <span />}
                                                </div>
                                            ))}
                                        </div>
                                        <button type="button" className="pf-add-lote" onClick={addLote}>
                                            + Agregar otro lote
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Fecha de registro (solo edición) */}
                        {isEditing && initialData?.created_at && (
                            <div className="pf-row pf-row--1" style={{ marginTop: 12 }}>
                                <div className="pf-field">
                                    <label className="pf-label">📅 Fecha de Registro</label>
                                    <input
                                        type="text"
                                        className="pf-input pf-input--readonly"
                                        disabled
                                        value={new Date(initialData.created_at).toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' })}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Nota campos obligatorios */}
                    <p className="pf-required-note">ℹ️ Los campos marcados con <span className="pf-req">*</span> son obligatorios.</p>
                </div>

                {/* ════════════════════════════════════════
                    SIDEBAR — RESUMEN RÁPIDO
                ════════════════════════════════════════ */}
                <aside className="pf-sidebar">

                    <div className="pf-sidebar-section">
                        <div className="pf-sidebar-title">📋 RESUMEN RÁPIDO</div>
                        <div className="pf-preview-card">
                            <div className="pf-preview-icon">{isJuguteria ? '🧩' : isBotica ? '💊' : '📦'}</div>
                            <div className="pf-preview-info">
                                <div className="pf-preview-name">{previewName}</div>
                                <div className="pf-preview-sub">{previewSub}</div>
                                <div className="pf-preview-lab">{previewLab}</div>
                            </div>
                        </div>
                        <div className="pf-preview-table">
                            <div className="pf-preview-row">
                                <span>Unidad de venta:</span>
                                <span>{formData.unit === 'unidad' ? 'Unidad' : formData.unit}</span>
                            </div>
                            <div className="pf-preview-row">
                                <span>Estado:</span>
                                <span className={`pf-status-dot ${formData.status === 'active' ? 'dot--active' : 'dot--inactive'}`}>
                                    ● {formData.status === 'active' ? 'Activo' : 'Inactivo'}
                                </span>
                            </div>
                            {!isEditing && (
                                <>
                                    <div className="pf-preview-row">
                                        <span>Stock inicial:</span>
                                        <span>{formData.stock || 0} unidades</span>
                                    </div>
                                    <div className="pf-preview-row">
                                        <span>Stock mínimo:</span>
                                        <span>{formData.min_stock || 0} unidades</span>
                                    </div>
                                </>
                            )}
                            <div className="pf-preview-row pf-preview-row--sep">
                                <span>Precio compra:</span>
                                <span>S/ {purchase > 0 ? purchase.toFixed(2) : '0.00'}</span>
                            </div>
                            <div className="pf-preview-row">
                                <span>Precio venta:</span>
                                <span>S/ {sale > 0 ? sale.toFixed(2) : '0.00'}</span>
                            </div>
                            <div className="pf-preview-row pf-preview-row--total">
                                <span>Ganancia estimada:</span>
                                <span className={ganancia >= 0 ? 'text--green' : 'text--red'}>
                                    S/ {ganancia.toFixed(2)} ({gananciaPct}%)
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Consejo */}
                    <div className="pf-sidebar-tip">
                        <span className="pf-tip-icon">💡</span>
                        <div>
                            <strong>Consejo</strong>
                            <p>Mantén tus precios y stock actualizados para evitar quiebres de inventario y pérdidas.</p>
                        </div>
                    </div>

                    {/* Atajos */}
                    <div className="pf-sidebar-section">
                        <div className="pf-sidebar-title">ATAJOS DE TECLADO</div>
                        <div className="pf-shortcuts">
                            <div className="pf-shortcut-row">
                                <span><kbd>Ctrl</kbd> + <kbd>S</kbd></span>
                                <span>Guardar medicamento</span>
                            </div>
                            {isBotica && (
                                <div className="pf-shortcut-row">
                                    <span><kbd>Ctrl</kbd> + <kbd>L</kbd></span>
                                    <span>Agregar lote</span>
                                </div>
                            )}
                            <div className="pf-shortcut-row">
                                <span><kbd>Esc</kbd></span>
                                <span>Cancelar / Volver</span>
                            </div>
                        </div>
                    </div>

                </aside>
            </form>
        </div>
    );
};

export default ProductForm;